import { ESymbolCategory, type IDisplayItem, type IDisplayItemProperty, type ISymbol } from "@kuika/kuika-cl-model";
import { getValues, type ScreenDesignInstance } from "./designer-context";
import { enrichTableFieldBindingSymbol, setDesignerPropertySymbol } from "./designer-property-actions";
import { TASK_FEED_FIELD_PREFIX } from "./task-feed-process-scope";

/**
 * KOA-18136 — finish the columns a task-feed scope just made possible.
 *
 * A table column bound to `data.<Param>` is only HALF a binding until the field
 * it names exists: `enrichTableFieldBindingSymbol` resolves the field against the
 * table's datasource and stamps `fieldToDisplay` / `fieldType` /
 * `fixedValue: "[datafield:…]"` / `tableLabelSourceType`, and React codegen keys
 * its value reader (`.Value`) on the `data` prefix carried inside
 * `fieldToDisplay`. Without the stamp the column generates as the bag ENTRY
 * object instead of the value.
 *
 * That enrichment runs when the binding is written — which is BEFORE the scope
 * exists: the Designer renders the table (creating the action instance the scope
 * is stored on) and only then can the feed be scoped. So the first write always
 * misses, through no fault of the caller.
 *
 * Rather than force an order onto the caller — the exact kind of requirement a
 * weaker model drops — the scope write finishes the job itself: re-enrich every
 * `data.*` column on the screen once the fields are real. Idempotent, and a
 * column whose field still does not resolve is left exactly as it was (the
 * enrichment returns the same symbol reference), so this can never make a binding
 * worse.
 */

/** One repaired column, for the operation's own report. */
export interface ITaskFeedColumnRepair {
  readonly kuikaId: string;
  readonly propertyName: string;
  readonly fieldName: string;
}

function isProcessRowFieldSymbol(symbol: ISymbol | undefined): boolean {
  if (!symbol || symbol.category !== ESymbolCategory.actionField) return false;
  const fieldName: string = String(symbol.fieldName ?? "");
  return fieldName.toLowerCase().startsWith(`${TASK_FEED_FIELD_PREFIX}.`);
}

/**
 * Re-enrich every process-row-field column on the active screen.
 *
 * Deliberately screen-wide rather than scoped to one table: a screen may hold two
 * task lists, the cell→table walk already decides which datasource each column
 * belongs to, and a column whose own table is unscoped simply does not resolve and
 * is left alone. Narrowing it by action id would add a second, weaker copy of that
 * walk for no gain.
 */
export function repairTaskFeedColumns(sdInstance: ScreenDesignInstance): ITaskFeedColumnRepair[] {
  const repaired: ITaskFeedColumnRepair[] = [];
  const state = getValues(sdInstance);
  const dictionary = state?.screenDesignDictionary;
  if (!dictionary) return repaired;

  for (const key of Object.keys(dictionary)) {
    const di: IDisplayItem | undefined = dictionary[key as unknown as number];
    const properties: IDisplayItemProperty[] = di?.item?.properties ?? [];
    for (const property of properties) {
      if (!isProcessRowFieldSymbol(property.symbol)) continue;
      // The dictionary is keyed BY kuikaId — that key is the id, no field lookup needed.
      const kuikaId: string = String(key);
      const enriched: ISymbol = enrichTableFieldBindingSymbol(property.symbol as ISymbol, sdInstance, kuikaId);
      // Same reference = the field did not resolve. Never write a symbol the
      // enrichment refused to improve.
      if (enriched === property.symbol) continue;
      setDesignerPropertySymbol(sdInstance, {
        kuikaId,
        propertyName: property.name,
        symbol: enriched
      });
      repaired.push({
        kuikaId,
        propertyName: property.name,
        fieldName: String((property.symbol as ISymbol).fieldName ?? "")
      });
    }
  }
  return repaired;
}
